import { Paper } from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setDialogHandle } from "../../../actions/dialog";
import { OrderTypesPickup } from "../../../formSchemas/ordersSetting.form";
import useTask from "../../../hooks/useTask";
import IVenueSettings from "../../../interfaces/IVenueSettings";
import { IReduxAppState } from "../../../reducers";
import { HTTPMethods } from "../../../yoello-lib/modules/API/API";
import APIExtensions from "../../../yoello-lib/modules/API2/APIDefs";
import { useAPIV2 } from "../../../yoello-lib/modules/API2/useAPIv2";
import { useSnackbarMessages } from "../../../yoello-lib/modules/components/snackbar/SnackbarContext";
import { useOrderBacklog } from "../../orders/hooks/useOrderBacklog";
import ListLoading from "../../util/Loading";
import { CollectionAlert } from "../Ui/CollectionAlert";
import { OutstandingCollectionDialog } from "../Ui/OutstandingCollectionDialog";
import { PickupForms } from "./PickupForms";
import Styles from '../../../scss/collectionPoint.module.scss';

export function PickupContainer() {
    const { t } = useTranslation();
    const { sendSnackbarMessage } = useSnackbarMessages()!;
    const { increaseDoneTasks } = useTask();

    const dispatch = useDispatch();
    const [collectionSettings, setCollectionSettings] = useState<any>(null);

    const [isLoading, setIsLoading] = useState(false);
    const selectedVenueId = useSelector((state: IReduxAppState) => state.selectedVenueId)
    const { callAPI, apiData } = useAPIV2<any>(APIExtensions.collectionPoints, {
        yoelloAPIParams: {
            venue_id: selectedVenueId, include_disabled: true
        },
        params: {
            "sort_direction": "asc",
            "sort_field": "created_on"
        }
    });
    const { apiData: settings, callAPI: callSettings } = useAPIV2<IVenueSettings>(APIExtensions.venueSettings,
        {
            selectorKeyParams: { venue_id: selectedVenueId }, yoelloAPIParams: { venue_id: selectedVenueId, include_disabled: true, include_hidden: true }, dispatcherParams: [selectedVenueId]
        });
    const { checkOrderBacklog } = useOrderBacklog(true)
    /**We keep old list in memory */
    const ref = useRef<any>();

    useEffect(() => {
        if (settings && Array.isArray(apiData)) {
            setCollectionSettings({
                pickup_now_enabled: settings.pickup_now_enabled,
                pickup_now_auto_close_orders_enabled: settings.pickup_now_auto_close_orders_enabled,
                min_pickup_now_price: settings.min_pickup_now_price,
                collection_points: apiData.map((item) => {
                    return { ...item, itemsId: item.id }
                })
            });
            ref.current = [...apiData];
        }
    }, [apiData, settings]);

    async function onSubmit(data: any) {
        if (data.pickup_now_enabled !== null) {
            try {
                const collection_points = data.collection_points.map((cp: any) => {
                    return { ...cp, itemsId: undefined, id: cp?.itemsId }
                });

                setIsLoading(true);
                const isAllDisabled = isAllCollectionDisabled(collection_points);
                if (isAllDisabled) {
                    const { data: orderbacklog } = await checkOrderBacklog();
                    if (orderbacklog && orderbacklog.count > 0) {
                        dispatch(setDialogHandle({
                            Component: OutstandingCollectionDialog,
                            canClose: true,
                            dialogTitle: "",
                            isFormDialog: false,
                            styles: { className: Styles.CollectionPointDialog }
                        }));
                        await callAPI(HTTPMethods.GET);
                        return;
                    };
                }
                if (data.pickup_now_enabled
                    && !isCollectionEnabled(collection_points)) {
                    sendSnackbarMessage(t('forms.orders.pickup.errors.enabledNone'), 'error');
                    await callAPI(HTTPMethods.GET);
                    return;
                }

                const deleteList = getDeletedPoints(collection_points, ref.current);

                await callSettings(HTTPMethods.PATCH, {
                    query: settings?.id!,
                    body: {
                        pickup_now_enabled: data.pickup_now_enabled,
                        pickup_now_auto_close_orders_enabled: data.pickup_now_auto_close_orders_enabled,
                        min_pickup_now_price: data.min_pickup_now_price,
                    }
                });

                const workload = [];
                for (const items in collection_points) {
                    const newObj = collection_points[items];
                    workload.push(saveCollectionPoint(callAPI, newObj, selectedVenueId));
                }
                const key = `order-filters-${selectedVenueId}-collection`;
                const item = localStorage.getItem(key);

                for (const deleteItem of deleteList) {
                    workload.push(deleteCollectionPoint(callAPI, deleteItem));
                }
                /**Deletes the keys from the filter */
                if (deleteList.length > 0 && item !== null && Object.keys(JSON.parse(item)).length > 0) {
                    const newObj = JSON.parse(item);
                    for (const { id } of deleteList) {
                        delete newObj[id];
                    }
                    localStorage.setItem(key, JSON.stringify(newObj));
                }
                await Promise.all(workload);
                increaseDoneTasks();
                await callAPI(HTTPMethods.GET);
                sendSnackbarMessage(t('snackbar.updateSuccess'), 'success');
            } catch (e) {
                sendSnackbarMessage(t('forms.orders.pickup.errors.apiError'), 'error');
            } finally {
                setIsLoading(false);
            }
        }
    }
    function renderForms() {
        const formRows = OrderTypesPickup(settings?.pickup_enabled!, t);
        return formRows.map((rowItem) => {
            return <PickupForms {...rowItem} isLoading={isLoading} formData={collectionSettings} handleSubmit={onSubmit} />
        })
    }
    return <div>
        <CollectionAlert isDisabled={settings?.pickup_enabled === true} label="collectionEnabled" />
        {!isLoading && collectionSettings ? renderForms()
            :
            <Paper>
                <div style={{
                    display: "flex",
                    maxWidth: '60rem',
                    height: '50rem',
                    alignItems: "center",
                    justifyContent: "center"
                }}>
                    <ListLoading />
                </div>
            </Paper>
        }
    </div >
}

async function saveCollectionPoint(api: any, body: any, venueId: string) {
    const method = body?.id ? HTTPMethods.PATCH : HTTPMethods.POST;
    const query = body?.id ? body?.id : undefined;
    delete body?.id;
    return await api(method, { body: { ...body, venue_id: venueId }, query });
}

async function deleteCollectionPoint(api: any, body: any) {
    const method = HTTPMethods.DELETE;
    const query = body?.id;
    return await api(method, { query });
}

function getDeletedPoints(collectionPointNew: any, collectionPointOld: any) {
    const deleteItems = [];
    for (const item in collectionPointOld) {
        const obj = collectionPointOld[item];
        let isFound = false;
        for (const newItem in collectionPointNew) {
            const newObj = collectionPointNew[newItem]
            if (newObj.id && newObj.id === obj.id) {
                isFound = true;
                break;
            }
        }
        if (!isFound) {
            deleteItems.push(obj);
        }
    }
    return deleteItems;
}

function isCollectionEnabled(collectionNewPoints: Record<string, any>) {
    for (const item in collectionNewPoints) {
        const obj = collectionNewPoints[item];
        if (obj.enabled) {
            return true;
        }
    }
    return false;
}

function isAllCollectionDisabled(collectionPoints: Record<string, any>) {
    let counter = 0;
    let disabledCounter = 0;
    for (const item in collectionPoints) {
        counter++;
        const obj = collectionPoints[item];
        if (!obj.enabled) {
            disabledCounter++;
        }
    }
    return counter === disabledCounter;
}
